import React, { useState } from "react";
import { useTranslation } from "react-i18next";
import { Table, type TableColumn, type TableAction } from "@/components/Table";
import type { RoleItem } from "@/pages/Roles/types";
import { Icon } from "@/components/Icon";
import { Badge } from "@/components/Badge";
import { ModalDelete } from "@/components";
import { EditRoleModal } from "@/pages/Roles/EditRoleModal";
import { roleManagementApi } from "@/lib/api";
import { useToast } from "@/context/ToastContext";

import { useAuth } from "@/context/AuthContext";

interface RolesTableProps {
    items: RoleItem[];
    loading: boolean;
    search: string;
    onSearchChange: (value: string) => void;
    onUpdateItem: (item: RoleItem) => void;
    onDeleteItem: (id: number) => void;
    onRefresh: () => void;
    className?: string;
    error?: string | null;
}
export const RolesTable = ({
    items,
    loading,
    search,
    onSearchChange,
    onUpdateItem,
    onDeleteItem,
    onRefresh,
    error,
    className = "",
}: RolesTableProps) => {
    const { t } = useTranslation("global");
    const { isAdmin } = useAuth();
    const { showSuccess, showError } = useToast();
    const [deleteTarget, setDeleteTarget] = useState<RoleItem | null>(null);
    const [editTarget, setEditTarget] = useState<RoleItem | null>(null);
    const [isDeleting, setIsDeleting] = useState(false);

    const handleConfirmDelete = async () => {
        if (!deleteTarget) return;
        setIsDeleting(true);
        try {
            await roleManagementApi.delete(deleteTarget.id);
            showSuccess(t("roles.deleted"));
            const id = deleteTarget.id;
            setDeleteTarget(null);
            onDeleteItem(id);
        } catch (err) {
            showError(t("roles.error_delete"));
        } finally {
            setIsDeleting(false);
        }
    };

    const tableColumns: TableColumn<RoleItem>[] = [
        /*
        {
            key: 'id',
            label: 'ID'
        },*/
        {
            key: "name",
            label: t("roles.name"),
        },
        {
            key: "tag",
            label: t("roles.tag"),
        },
        {
            key: "description",
            label: t("roles.description"),
        },
    ];

    const tableActions: TableAction<RoleItem>[] = [
        {
            label: t("table.edit"),
            icon: (
                <Icon
                    value="pencil"
                    size={14}
                    className="text-slate-600 dark:text-zinc-300"
                />
            ),
            onClick: (row) => {
                setEditTarget(row);
            },
        },
        {
            label: t("table.delete"),
            icon: <Icon value="trash" size={14} className="text-rose-600" />,
            variant: "delete",
            onClick: (row) => {
                setDeleteTarget(row);
            },
        },
    ];

    return (
        <div className={className}>
            <Table
                viewKey="vigepra_roles"
                viewStorageKey="vigepra_role_views"
                appearance="workspace"
                error={error}
                onRetry={onRefresh}
                columns={tableColumns}
                data={items}
                actions={isAdmin ? tableActions : []}
                actionsPosition="right"
                searchValue={search}
                onSearchChange={onSearchChange}
                loading={loading}
                filterbox={false}
                defaultSortKey="name"
                defaultSortOrder="asc"
            />
            {deleteTarget && (
                <ModalDelete
                    isOpen={!!deleteTarget}
                    onClose={() => !isDeleting && setDeleteTarget(null)}
                    itemName={deleteTarget.name}
                    badgeText={deleteTarget.tag}
                    itemDescription={deleteTarget.description}
                    onConfirm={handleConfirmDelete}
                    isDeleting={isDeleting}
                />
            )}

            {editTarget && (
                <EditRoleModal
                    isOpen={!!editTarget}
                    onClose={() => setEditTarget(null)}
                    role={editTarget}
                    onSuccess={(updatedRole) => {
                        onUpdateItem(updatedRole);
                    }}
                />
            )}
        </div>
    );
};
